import { Component, OnInit, SecurityContext } from '@angular/core';
import { User } from '../../../models/User/user';
import { OperationError } from '../../../models/server-result/operation-error';
import { DocumentForecast } from '../../../models/dashboard/document-forecast';
import { VerticalBarChart } from '../../../models/charts/vertical-bar-chart';
import { ReportingApiService } from '../../../server-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { AppContextService } from '../../../services/app-context.service';
import { TranslateService } from '../../../services/translate/translate.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { LogService } from '../../../services/log/log.service';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { OperationResult } from '../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmingContract } from '../../../models/contracts/confirming/confirming-contract';
import { ConfirmingContractsSearchCriteria } from '../../../models/contracts/confirming/confirming-contracts-search-criteria';
import { DocumentForecastByState } from '../../../models/dashboard/document-forecast-by-paymentstate';
import { DomSanitizer } from '@angular/platform-browser';
import { PendingApprovalDocumentsAmountDto } from '../../../models/dashboard/pending-approval-documents-amount-dto';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';

type DocumentForecastSeriesData = {
	PaymentState: string;
	TotalAmount: string;
};

type SeriesDetail = {
	name: string;
	value: string;
};

type SeriesData = {
	name: string;
	series: SeriesDetail[];
};

type CurrencyViewModel = {
	Currency: string;
	Selected: boolean;
};

@Component({
	selector: 'app-dashboard-buyer',
	templateUrl: './dashboard-buyer.component.html',
	styleUrls: ['../private-home.component.scss', './dashboard-buyer.component.scss'],
	standalone: false,
})
export class DashboardBuyerComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;
	public documentForecast: DocumentForecast[] = [];
	public contractsCount: number = 0;
	public contractsAvailableAmount: number = 0;
	public contractedAmount: number = 0;
	public suppliersActiveConnections: number = 0;
	public contractsAvailableAmountRatio: number = 0;
	public notPaidDocuments: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public confirmingPendingApprovalDocumentsAmount: number = 0;

	public documentForecastChart: VerticalBarChart = new VerticalBarChart();

	public selectedCurrency: string = '';
	public orderedCurrencies: CurrencyViewModel[] = [];
	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingContractsAvailableAmount: boolean = false;
	public loadingContractedAmount: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;
	public loadingConfirmingPendingApprovalDocumentsAmount: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private appContext: AppContextService,
		private translateService: TranslateService,
		private toasterService: ToasterService,
		private logger: LogService,
		private dateLocale: DateLocalePipe,
		private domSanitizer: DomSanitizer,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.getContracts();

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			this.documentForecastChart.View = [];
			this.documentForecastChart.ShowXAxis = true;
			this.documentForecastChart.ShowLegend = false;
			this.documentForecastChart.LegendTitle = '';
			this.documentForecastChart.ShowYAxis = true;
			this.documentForecastChart.ShowXAxisLabel = true;
			this.documentForecastChart.XAxisLabel = this.translateService.translate('dashboard-buyer.chart.buyerConfirmedPaymentDate');
			this.documentForecastChart.ShowYAxisLabel = true;
			this.documentForecastChart.YAxisLabel = this.selectedCurrency;
			this.documentForecastChart.ColorScheme = {
				domain: ['#3949ab', '#ff6d6e', '#40c9c5', '#f6c951', '#48aeff', '#6E47C0', '#88D4FF', '#C392FF'],
			};
		});
	}

	private getNotPaidDocumentsSum(currency: string): void {
		this.loadingNotPaidDocuments = true;
		this.notPaidDocuments = 0;
		this.reportingApiService.getNotPaidDocumentsSum(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingNotPaidDocuments = false;
				opResult.IsValid
					? (this.notPaidDocuments = opResult.Data)
					: this.logger.error('[DashboardBuyerComponent] Invalid result from getNotPaidDocumentsSum()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingNotPaidDocuments = false;
				this.logger.error(
					'[DashboardBuyerComponent] Invalid result from getNotPaidDocumentsSum()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getCurrentFundingRequestsAmount(currency: string): void {
		this.loadingCurrentFundingRequestsAmount = true;
		this.currentFundingRequestsAmount = 0;
		this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingCurrentFundingRequestsAmount = false;
				opResult.IsValid
					? (this.currentFundingRequestsAmount = opResult.Data)
					: this.logger.error('[DashboardBuyerComponent] Invalid result from getCurrentFundingRequestsAmount()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingCurrentFundingRequestsAmount = false;
				this.logger.error(
					'[DashboardBuyerComponent] Invalid result from getCurrentFundingRequestsAmount()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getSuppliersActiveConnections(currency: string): void {
		this.loadingSuppliersActiveConnections = true;
		this.suppliersActiveConnections = 0;
		this.reportingApiService.getConfirmingSuppliersActiveConnections(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingSuppliersActiveConnections = false;
				opResult.IsValid
					? (this.suppliersActiveConnections = opResult.Data)
					: this.logger.error('[DashboardBuyerComponent] Invalid result from getSuppliersActiveConnections()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingSuppliersActiveConnections = false;
				this.logger.error(
					'[DashboardBuyerComponent] Invalid result from getSuppliersActiveConnections()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getDocumentForecast(currency: string) {
		// Reset chart data before fetching new data
		this.documentForecastChart.Results = [];

		this.reportingApiService.getDailyDocumentForecast(currency).subscribe({
			next: (opResult: OperationResult<DocumentForecastByState[]>) => {
				if (opResult.IsValid) {
					let documentForecastByPaymentDate: { [paymentDate: string]: DocumentForecastSeriesData[] } = {};

					this.documentForecastChart.ShowLegend = opResult.Data.some((d) => !!d.DocumentPaymentState);

					for (let index = 0; index < opResult.Data.length; index++) {
						const element = opResult.Data[index];

						let key = this.dateLocale.transform(element.BuyerPaymentDate);
						let dictBuyerPaymentDate = documentForecastByPaymentDate[key];

						if (!dictBuyerPaymentDate) {
							dictBuyerPaymentDate = [];
						}

						var stateToDecode = element.DocumentPaymentState;
						var dicPaymentStateEntry = !!stateToDecode ? this.translateService.translate(stateToDecode, 'list-paymentState') : null;

						if (!!stateToDecode && !!dicPaymentStateEntry) {
							dictBuyerPaymentDate.push(<DocumentForecastSeriesData>{
								PaymentState: dicPaymentStateEntry,
								TotalAmount: element.TotalAmount,
							});
						}

						documentForecastByPaymentDate[key] = dictBuyerPaymentDate;
					}

					let results: SeriesData[] = [];

					Object.keys(documentForecastByPaymentDate).forEach(function (key) {
						results.push({
							name: key,
							series: documentForecastByPaymentDate[key].map(
								(r) =>
									<SeriesDetail>{
										name: r.PaymentState,
										value: r.TotalAmount,
									}
							),
						});
					});

					this.documentForecastChart.Results = results;
				} else {
					this.logger.error('[DashboardBuyerComponent] Invalid result from getDocumentForecast', opResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.logger.error(
					'[DashboardBuyerComponent] HttpError result from getDocumentForecast',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.loadingConfirmingPendingApprovalDocumentsAmount = true;
		this.confirmingPendingApprovalDocumentsAmount = 0;
		this.reportingApiService.getPendingApprovalDocumentsAmount(currency).subscribe({
			next: (opResult: OperationResult<PendingApprovalDocumentsAmountDto>) => {
				this.loadingConfirmingPendingApprovalDocumentsAmount = false;
				if (opResult.IsValid) {
					this.confirmingPendingApprovalDocumentsAmount = opResult.Data.Confirming;
				} else {
					this.logger.error('[DashboardBuyerComponent] Invalid result from getPendingApprovalDocumentsAmount', opResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingConfirmingPendingApprovalDocumentsAmount = false;
				this.logger.error(
					'[DashboardBuyerComponent] HttpError result from getPendingApprovalDocumentsAmount',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;

		this.resetValues();

		let currencies: CurrencyViewModel[] = [];
		let retrievedConfirmingContracts: ConfirmingContract[] = [];

		const criteria: ConfirmingContractsSearchCriteria = new ConfirmingContractsSearchCriteria();
		criteria.OrderBy = 'EndDate';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.BuyerIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];

		this.serverApiService.searchConfirmingContracts(criteria).subscribe({
			next: (getConfirmingContracts: OperationResult<ConfirmingContract[]>) => {
				if (getConfirmingContracts.IsValid) {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						getConfirmingContracts.Data.forEach((contract) => {
							retrievedConfirmingContracts.push(contract);

							if (currencies.findIndex((c) => c.Currency == contract.Currency) == -1)
								currencies.push(<CurrencyViewModel>{
									Currency: contract.Currency,
									Selected: false,
								});
						});

						this.orderedCurrencies = currencies.sort(function (c1: CurrencyViewModel, c2: CurrencyViewModel) {
							var currency1 = c1.Currency.toUpperCase();
							var currency2 = c2.Currency.toUpperCase();
							return currency1 < currency2 ? -1 : currency1 > currency2 ? 1 : 0;
						});

						if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
							let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;
							this.orderedCurrencies[0].Selected = true;

							let contractsOfInitialCurrency: ConfirmingContract[] = retrievedConfirmingContracts.filter(
								(c) => c.Currency == initialCurrencytoProcess
							);

							contractsOfInitialCurrency.forEach((contract) => {
								this.contractedAmount += contract.ContractedAmount;
								this.contractsAvailableAmount += contract.AvailableAmount;
								this.contractsAvailableAmountRatio =
									this.contractedAmount > 0 ? 100.0 * (this.contractsAvailableAmount / this.contractedAmount) : 0;
							});

							this.selectedCurrency = initialCurrencytoProcess;
							this.documentForecastChart.YAxisLabel = this.selectedCurrency;

							this.getNotPaidDocumentsSum(initialCurrencytoProcess);
							this.getDocumentForecast(initialCurrencytoProcess);
							this.getSuppliersActiveConnections(initialCurrencytoProcess);
							this.getCurrentFundingRequestsAmount(initialCurrencytoProcess);
							this.getPendingApprovalDocumentsAmount(initialCurrencytoProcess);
						}
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}

				// Set loading states to false after all calculations are complete
				this.loadingContractedAmount = false;
				this.loadingContractsAvailableAmount = false;
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private refreshContractDataForCurrency(currency: string) {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;

		const criteria: ConfirmingContractsSearchCriteria = new ConfirmingContractsSearchCriteria();
		criteria.OrderBy = 'EndDate';
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.BuyerIdList = this.loggedUser?.Companies.map((c) => c.Id!) ?? [];
		criteria.RestrictionCriteria.Currency = currency;

		this.serverApiService.searchConfirmingContracts(criteria).subscribe((getConfirmingContracts) => {
			if (!getConfirmingContracts.IsValid) {
				const errors = getConfirmingContracts.Errors;
				if (errors && errors.length > 0) {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: errors,
						autoClose: false,
					});
				}
			} else {
				if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
					getConfirmingContracts.Data.forEach((contract) => {
						this.contractedAmount += contract.ContractedAmount;
						this.contractsAvailableAmount += contract.AvailableAmount;
						this.contractsAvailableAmountRatio =
							this.contractedAmount > 0 ? 100.0 * (this.contractsAvailableAmount / this.contractedAmount) : 0;
					});
				}
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
		});
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;
		this.documentForecastChart.YAxisLabel = this.selectedCurrency;

		this.orderedCurrencies.forEach((orderedCurrency) => {
			orderedCurrency.Selected = orderedCurrency.Currency == currency;
		});

		this.refreshContractDataForCurrency(this.selectedCurrency);
		this.getNotPaidDocumentsSum(this.selectedCurrency);
		this.getDocumentForecast(this.selectedCurrency);
		this.getSuppliersActiveConnections(this.selectedCurrency);
		this.getCurrentFundingRequestsAmount(this.selectedCurrency);
		this.getPendingApprovalDocumentsAmount(this.selectedCurrency);
	}

	private resetValues() {
		this.contractsAvailableAmount = 0;
		this.contractsAvailableAmountRatio = 0;
		this.contractedAmount = 0;
		this.currentFundingRequestsAmount = 0;
		this.notPaidDocuments = 0;
		this.suppliersActiveConnections = 0;
		this.confirmingPendingApprovalDocumentsAmount = 0;

		// Reset chart data
		this.documentForecastChart.Results = [];
	}

	public notMultipleCurrencies(currencies: CurrencyViewModel[]): boolean {
		return currencies?.length <= 1;
	}

	public navigateToFilesIntegration(key: string): void {
		SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
	}

	public refreshAllData(): void {
		this.getContracts();
	}
}
